import React, { useEffect, useRef } from 'react';
import cn from 'classnames';

import BaseButton from 'components/base-button';
import { CloseIcon } from 'components/icons-new/close';
import { TrashIcon } from 'components/icons-new/trash';
import { TriangleExclamationMarkIcon } from 'components/icons-new/triangle-exclamation-mark';

import type { Notification as NotificationModel } from 'entities/notification';
import { useIntersectionObserver } from 'hooks/use-intersection-observer';

import { hasNotificationIssue } from '../../lib/has-notification-issue';
import { isNotificationRead } from '../../lib/is-notification-read';
import { formatDateWithRelativeTime } from './format-date-with-relative-time';

import classes from './notification.module.scss';
import { injectLink } from 'widgets/notifications/ui/notification/inject-link';

interface UseAutoMarkAsReadProps {
  notificationId: string;
  isRead: boolean;
  isUrgent: boolean;
  onMarkAsRead: (id: string) => void;
}

const useAutoMarkAsRead = ({ notificationId, isRead, isUrgent, onMarkAsRead }: UseAutoMarkAsReadProps) => {
  const timerRef = useRef<NodeJS.Timeout | null>(null);

  const { elementRef, hasTriggered } = useIntersectionObserver<HTMLDivElement>({
    threshold: 1,
    rootMargin: '0px',
    triggerOnce: false
  });

  useEffect(() => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }

    if (!hasTriggered || isUrgent || isRead) {
      return;
    }

    timerRef.current = setTimeout(() => {
      onMarkAsRead(notificationId);
      timerRef.current = null;
    }, 1000);

    return () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
        timerRef.current = null;
      }
    };
  }, [hasTriggered, notificationId, isRead, isUrgent, onMarkAsRead]);

  return elementRef;
};

interface NotificationProps {
  notification: NotificationModel;
  isUrgent?: boolean;

  onClose: () => void;
  onDeactivate: (id: string) => void;
  onMarkAsRead: (id: string) => void;
}

const Notification = ({ notification, isUrgent = false, onDeactivate, onClose, onMarkAsRead }: NotificationProps) => {
  const isRead = isNotificationRead(notification);

  const elementRef = useAutoMarkAsRead({
    notificationId: notification.id,
    isRead,
    isUrgent,
    onMarkAsRead
  });

  const handleClickCloseButton = () => {
    onMarkAsRead(notification.id);
    onClose();
  };

  const handleClickDeactivateButton = () => {
    onDeactivate(notification.id);

    if (isUrgent) {
      onClose();
    }
  };

  const issueStatus = hasNotificationIssue(notification);

  return (
    <li
      className={cn(classes.notification, {
        [classes['notification--not-read']]: !isRead
      })}
    >
      <div className={classes.notification__header} ref={elementRef}>
        <time className={classes['notification__creation-date']} dateTime={notification.attributes.createdon.value}>
          {formatDateWithRelativeTime(new Date(notification.attributes.createdon.value))}
        </time>
        <div className={classes['notification__header-actions']}>
          <BaseButton
            aria-label='Deactivate notification'
            className={classes.rounded}
            icon
            variant='tertiary'
            onClick={handleClickDeactivateButton}
          >
            <TrashIcon width={16} height={16} />
          </BaseButton>

          {isUrgent && (
            <BaseButton
              aria-label='Close notification'
              className={classes.rounded}
              icon
              variant='tertiary'
              onClick={handleClickCloseButton}
            >
              <CloseIcon width={16} height={16} />
            </BaseButton>
          )}
        </div>
      </div>
      <div
        className={cn(classes['notification__content'], {
          [classes['notification__content--warning']]: issueStatus
        })}
      >
        {issueStatus && (
          <TriangleExclamationMarkIcon
            className={classes['notification__warning-icon']}
            color='#FF9700'
            width={18}
            height={18}
          />
        )}
        <p className={classes['notification__message']}>
          {injectLink(notification.attributes.uds_text, notification.attributes.regardingObjectFromText)}
        </p>
      </div>
    </li>
  );
};

export default Notification;
